import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useOrganisation } from "@/api/organisation-api";
import { OrgBrand } from "@/components/OrgBrand";
import { useToast } from "@/hooks/use-toast";
import { AppDialog } from "@/components/ui/AppDialog";
import { useScreeningRecorder } from "@/hooks/useScreeningRecorder";
import { Movable } from "@/components/viewer/Movable";
import { RoomViewer } from "@/components/viewer/RoomViewer";
import {
  advanceScreening,
  beginScreening,
  fetchScreeningState,
  finaliseVideo,
  startScreeningAnswer,
  type ScreeningState,
} from "@/api/tests-api";

/**
 * The video screening round, from the candidate's side.
 *
 * ROUND 2'S QUESTIONS (2026-11-02): the questions, the intro and the end
 * card are drawn to `docs/design/round2-questions.html` -- one question
 * at a time rolling in, thinking time, then the answer, then a short
 * "time's up" -- in that design's own type and colours (`.sk-r2-*`). The
 * CAMERA CARD keeps the earlier design below, as asked. What changed in
 * the design's intro, on instruction: it told the candidate to start
 * recording in their own interview software; here the app records, so
 * its first step is the camera and its Start button starts the round.
 *
 * The camera card is built to
 * `docs/design/Candidate Screening - Standalone.html`, whose
 * values are transcribed into `.sk-vr-*` in styles/components.css rather
 * than written here as Tailwind: `index.css` re-declares `text-[10px]`
 * through `text-[13px]` 15% larger, so arbitrary sizes would bump some
 * of the design's type and not the rest.
 *
 * WHAT THE DESIGN DOES THAT THIS DOES NOT, all on instruction:
 *
 * * its section tabs jump between sections — here the stepper is state,
 *   not a control, and advances only on NEXT or the clock;
 * * Retake, Skip and Save & exit are gone. One attempt, no going back;
 * * a question's row shows Play once answered. There is no playback at
 *   all: the recording goes to the server and that is the end of the
 *   candidate's part;
 * * future questions are not listed. The design shows every question in
 *   a section; revealing them early would let a candidate prepare an
 *   answer they are supposed to give unrehearsed, which is the point of
 *   the round.
 *
 * THE CLOCK ON SCREEN IS NOT THE CLOCK THAT DECIDES. The server stamps
 * when a question was served and closes it when its allowance is spent;
 * this counts down from the number the server last sent, re-anchoring
 * every time it answers. A candidate who stops the page's timer buys
 * nothing.
 */

const POLL_MS = 15_000;

function mmss(total: number): string {
  const s = Math.max(0, Math.round(total));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** The design's own clock: m:ss, no leading zero on the minutes. */
function clockStr(total: number): string {
  const s = Math.max(0, Math.round(total));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** "1½ minutes", "45 seconds" -- the design's wording for an allowance. */
function durationWords(s: number): string {
  if (s < 60) return `${s} seconds`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (r === 0) return `${m} minute${m > 1 ? "s" : ""}`;
  if (r === 30) return `${m}½ minutes`;
  return `${m} min ${r} sec`;
}

const NUMBER_WORDS = [
  "no", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen", "twenty",
];

/** "fifteen questions", as the design writes it; digits past twenty. */
function inWords(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

function Modebar() {
  return (
    <nav className="sk-r2-modebar">
      <div className="sk-r2-modebar-inner">
        <span className="sk-r2-brand">
          <OrgBrand variant="topbar" />
          <em>Round 2 · Questions</em>
        </span>
      </div>
    </nav>
  );
}

export function VideoScreeningPage() {
  const { attemptId = "" } = useParams<{ attemptId: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();

  const recorder = useScreeningRecorder(attemptId);
  const [state, setState] = useState<ScreeningState | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  // Submitted IN THIS VISIT: the page stays and says so, rather than
  // jumping back to the list -- the candidate's last sight of the round
  // must be that it was received.
  const [submitted, setSubmitted] = useState(false);
  const submittingRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // The countdown re-anchors whenever the server answers, so drift
  // between the two can never accumulate.
  const [remaining, setRemaining] = useState<number | null>(null);
  // Round 2's thinking time, anchored to the same server answer.
  const [thinkLeft, setThinkLeft] = useState<number | null>(null);
  const anchorRef = useRef<{
    at: number;
    seconds: number;
    think: number | null;
  } | null>(null);

  const apply = useCallback((next: ScreeningState) => {
    setState(next);
    setThinkLeft(next.think_seconds_remaining ?? null);
    if (next.seconds_remaining == null) {
      anchorRef.current = null;
      setRemaining(null);
      return;
    }
    anchorRef.current = {
      at: performance.now(),
      seconds: next.seconds_remaining,
      think: next.think_seconds_remaining ?? null,
    };
    setRemaining(next.seconds_remaining);
  }, []);

  useEffect(() => {
    if (recorder.stream && videoRef.current) {
      videoRef.current.srcObject = recorder.stream;
    }
  }, [recorder.stream]);

  // Poll: the state AND the heartbeat. `last_seen_at` moving is what
  // lets the server work out how long a candidate was gone and give the
  // time back, so this is not a spare request.
  useEffect(() => {
    if (!attemptId) return undefined;
    let live = true;
    const read = async () => {
      try {
        const next = await fetchScreeningState(attemptId);
        if (live) apply(next);
      } catch {
        // Silent on purpose: losing the network mid-answer is the case
        // the whole round is designed around, and a toast per failed
        // poll would bury the candidate in noise while they speak.
      }
    };
    // ONE READ ALWAYS, so the page can tell whether the round is
    // finished, running or not yet begun. But the REPEAT only while
    // they are actually answering -- see below.
    void read();
    if (!checked) return () => {
      live = false;
    };

    // THIS POLL IS THE HEARTBEAT, so it must not beat while the
    // candidate cannot answer.
    //
    // `last_seen_at` moving is how the server decides they are present,
    // and it is what `absorb_absence` measures a pause against. Polling
    // on a page where the camera is OFF -- after a reload, before they
    // press Resume -- told the server they were answering while they
    // were locked out of doing so. Their allowance drained, the client
    // advanced when it hit zero, and a question they never got to speak
    // to was closed. On the last item that ended the sitting outright.
    const id = window.setInterval(read, POLL_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [attemptId, apply, checked]);

  // Tick locally between polls -- and ONLY while answering, for the
  // same reason the poll does. A clock counting down on a screen whose
  // camera is off is telling the candidate they are losing time they
  // are not, in fact, losing.
  useEffect(() => {
    if (remaining == null || !checked) return undefined;
    const id = window.setInterval(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const gone = (performance.now() - anchor.at) / 1000;
      setRemaining(Math.max(0, Math.round(anchor.seconds - gone)));
      if (anchor.think != null) {
        setThinkLeft(Math.max(0, Math.round(anchor.think - gone)));
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [remaining == null, checked]);

  const advance = useCallback(
    async (endedBy: "next" | "timer") => {
      if (busy || !state || state.finished) return;
      setBusy(true);
      try {
        apply(await advanceScreening(attemptId, recorder.elapsed, endedBy));
      } catch {
        toast({
          title: "Could not move to the next question",
          description: "Keep answering — this will retry on its own.",
          variant: "error",
        });
      } finally {
        setBusy(false);
      }
    },
    [attemptId, busy, state, recorder.elapsed, apply, toast],
  );

  // "I'm ready to answer": the SERVER starts the answer clock, so the
  // thinking time not used is gone rather than banked.
  const readyToAnswer = useCallback(async () => {
    if (busy || !state || state.finished) return;
    setBusy(true);
    try {
      apply(await startScreeningAnswer(attemptId, recorder.elapsed));
    } catch {
      toast({
        title: "Could not start your answer",
        description: "Keep going — your thinking time ends on its own.",
        variant: "error",
      });
    } finally {
      setBusy(false);
    }
  }, [attemptId, busy, state, recorder.elapsed, apply, toast]);

  // THE TIMER ADVANCES BY ITSELF. The candidate is mid-sentence when it
  // runs out and must not have to press anything; the server would close
  // the question anyway on the next request, so doing it here only makes
  // the screen agree with what has already happened.
  useEffect(() => {
    if (remaining !== 0 || !state || state.finished || busy) return;
    // NOT WHILE THE CAMERA IS OFF. Advancing CLOSES a question, and a
    // question closed while the candidate could not speak to it is an
    // answer they never got to give -- on the last item it ended the
    // whole sitting and submitted it.
    if (!checked || recorder.phase !== "recording") return;
    void advance("timer");
  }, [remaining, state, busy, advance, checked, recorder.phase]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await recorder.stop();
      await finaliseVideo(attemptId);
      recorder.release();
      setSubmitted(true);
      setBusy(false);
    } catch {
      // The automatic submit below gives up here and the button comes
      // back, because a candidate who cannot retry has lost a sitting
      // they get one of.
      submittingRef.current = false;
      setSubmitFailed(true);
      toast({
        title: "Could not submit",
        description:
          "Nothing you recorded has been lost. Try again in a moment.",
        variant: "error",
      });
      setBusy(false);
    }
  }, [attemptId, recorder, toast]);

  // SUBMITTING IS A CONSEQUENCE OF THE ROUND ENDING, NOT A SEPARATE ACT.
  //
  // There is no playback and no correction: once the last answer is
  // given there is nothing a candidate could still decide, so asking
  // them to press one more button only leaves the CAMERA RUNNING while
  // they work out what it is for. It fires on `finished` rather than in
  // the button handler so that the clock running out on the last
  // question ends the sitting exactly as pressing the button does.
  useEffect(() => {
    // `finished` stays true for a SUBMITTED attempt, so without the
    // status check a reload of a completed sitting would try to submit
    // it again -- stopping a recorder that was never started.
    if (!state?.finished || submittingRef.current) return;
    if (state.status !== "IN_PROGRESS") return;
    submittingRef.current = true;
    void submit();
  }, [state?.finished, state?.status, submit]);

  const beginRound = useCallback(async () => {
    setBusy(true);
    // TWO FAILURES WITH NOTHING IN COMMON, told apart.
    //
    // A bare `catch` around both of these reported "Could not start the
    // round" whether the camera had refused or the SERVER had -- and
    // when the server was refusing with a precise 409 (the attempt had
    // never been started), that message sent somebody looking at their
    // webcam. Same shape as `act()` swallowing a 422 on the reporting
    // screen; see CLAUDE.md, "catch {} cost the diagnosis".
    try {
      recorder.start();
    } catch {
      setBusy(false);
      toast({
        title: "Your camera or microphone could not be started",
        description:
          "Check that the browser is allowed to use them, then try again.",
        variant: "error",
      });
      return;
    }
    try {
      apply(await beginScreening(attemptId));
      setChecked(true);
      // FOLDED THE MOMENT THE RECORDING STARTS (asked for 2026-10-07): the
      // question is what to look at now, and the folded rail blinks red
      // so the camera is never forgotten. Unfolding stays one press away.
      setFolded(true);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast({
        title: detail ?? "Could not start the round",
        ...(detail
          ? {}
          : {
              description:
                "Nothing has been lost. Check your connection and try again.",
            }),
        variant: "error",
      });
    } finally {
      setBusy(false);
    }
  }, [attemptId, recorder, apply, toast]);

  const recording = recorder.phase === "recording";
  const question = state?.question ?? null;
  // A CASE ITEM. The candidate dictates a study rather than answering a
  // question, so the images need the room -- but the camera stays on
  // them, because the dictation is the part where knowing who is
  // speaking matters most.
  const caseId =
    (question as { assignment_case_id?: string } | null)?.assignment_case_id ??
    null;
  // The reading room's dock while it is full screen. The camera card and
  // the question MOVE into it -- the recording is one continuous take with
  // no retake, so nothing here may remount.
  const [dock, setDock] = useState<HTMLElement | null>(null);
  // The camera column FOLDED to a slim panel. Folded when the round
  // starts, and kept across questions.
  const [folded, setFolded] = useState(false);
  const lastQuestion = !!state && state.index >= state.total - 1;

  // WHETHER THE ROUND HAS STARTED IS THE SERVER'S ANSWER, NOT A LOCAL
  // FLAG. `checked` lives in component state and a RELOAD resets it, so
  // the page offered "Start the round" again on a sitting that was
  // already over -- and pressing it turned the camera back on and
  // recorded a candidate who had finished. The attempt's own status is
  // the only thing that survives a refresh.
  const closed =
    !!state && state.status !== "NOT_STARTED" && state.status !== "IN_PROGRESS";
  const begun = !!state && state.status !== "NOT_STARTED";
  // Folding only while the round runs, and not while docked in the
  // reading room's full screen, where the dock already is the side panel.
  const running = checked && begun && !state?.finished;
  const foldedHere = folded && running && !(caseId && dock);

  // Leaving mid-round is survivable -- the round resumes -- but it costs
  // the candidate time and a connection drop, so the browser asks first,
  // as the design does.
  useEffect(() => {
    if (!running) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [running]);

  // ---- round 2's phases -------------------------------------------------
  //
  // THINK, ANSWER, TIME'S UP -- all three read off the two numbers the
  // server sent, so the screen cannot disagree with the clock that
  // decides. A question from before round 2 (and every case item) has
  // no thinking time and no grace, and is simply "Answer now".
  const config = (question?.config ?? {}) as {
    title?: string;
    think_seconds?: number;
    answer_seconds?: number;
    allotted_seconds?: number;
    warmup?: boolean;
  };
  const warmup = !!config.warmup;
  const grace = state?.grace_seconds ?? 0;
  const phase: "think" | "answer" | "up" =
    thinkLeft != null && thinkLeft > 0
      ? "think"
      : grace > 0 && remaining != null && remaining <= grace
        ? "up"
        : "answer";
  const answerSeconds = config.answer_seconds ?? config.allotted_seconds ?? 0;
  const phaseLeft =
    phase === "think"
      ? (thinkLeft ?? 0)
      : phase === "up"
        ? (remaining ?? 0)
        : Math.max(0, (remaining ?? 0) - grace);
  const phaseTotal =
    phase === "think" ? (config.think_seconds ?? 0) : phase === "up" ? grace : answerSeconds;
  const filled =
    phaseTotal > 0 ? Math.min(100, Math.max(0, 100 - (phaseLeft / phaseTotal) * 100)) : 0;
  const scoredTotal = state?.scored_total ?? state?.total ?? 0;
  const title = config.title || (caseId ? "Your report" : question?.section_title) || "Question";
  const kicker = warmup
    ? "Warm-up · not scored"
    : `Question ${state?.scored_position ?? (state?.index ?? 0) + 1} of ${scoredTotal}` +
      (caseId ? " · Case" : question?.section_title ? ` · ${question.section_title}` : "");
  const finishLabel = lastQuestion ? "Finish" : "Next question";

  const org = useOrganisation().data;
  const orgName = org?.name || "The practice";

  // THE SUCCESS SCREEN, in this visit or on any later one: "Thank you",
  // and that the tab can be closed -- a candidate who has just been
  // filmed for forty minutes should not have to wonder whether it went.
  if (submitted || (closed && state?.status === "SUBMITTED")) {
    return (
      <div className="sk-vr sk-r2" data-cy="video-screening">
        <Modebar />
        <div className="sk-r2-col mx-auto px-4 py-[18px]">
          <div className="sk-r2-pcard sk-r2-endcard" data-cy="vr-submitted" role="status">
            <div className="sk-r2-tick" aria-hidden>
              ✓
            </div>
            <h1>Thank you</h1>
            <p {...(submitted ? {} : { "data-cy": "vr-closed" })}>
              Your exam is submitted. You can safely close the browser tab.
            </p>
            <button
              type="button"
              data-cy="btn-vr-back"
              className="sk-r2-btn"
              onClick={() => navigate("/tests")}
            >
              Back to my assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (closed) {
    // NOTHING TO OFFER AND NOTHING TO RECORD. One attempt, no retake:
    // there is no control on this screen a finished candidate should be
    // able to press, so none are drawn rather than drawn and disabled.
    return (
      <div className="sk-vr sk-r2" data-cy="video-screening">
        <Modebar />
        <div className="sk-r2-col mx-auto px-4 py-[18px]">
          <div className="sk-r2-pcard sk-r2-endcard">
            <div className="sk-r2-tick" aria-hidden>
              ✓
            </div>
            <h1>This round is finished</h1>
            <p data-cy="vr-closed">
              This sitting is closed and cannot be reopened.
            </p>
            <button
              type="button"
              data-cy="btn-vr-back"
              className="sk-r2-btn sk-r2-btn-primary"
              onClick={() => navigate("/tests")}
            >
              Back to my assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- what sits beside the camera -------------------------------------
  //
  // Built to `docs/design/round2-questions.html` (supplied 2026-11-02) and
  // drawn in ITS type and colours (`.sk-r2-*`); the camera card keeps the
  // earlier screen's own look, as asked. Intro, then one question at a
  // time rolling in, then the end card.
  const intro = (
    <div className="sk-r2-pcard" data-cy="vr-intro">
      <p className="sk-r2-qprog">{orgName} · Round 2</p>
      <h1>Your interview questions</h1>
      {begun ? (
        <>
          <p>
            Your round is already running. Turn your camera back on beside
            this card, then press Resume to carry on where you left off.
          </p>
          <button
            type="button"
            data-cy="btn-vr-resume"
            className="sk-r2-btn sk-r2-btn-primary sk-r2-btn-wide"
            disabled={!recorder.stream || busy}
            onClick={() => void beginRound()}
          >
            Resume
          </button>
        </>
      ) : (
        <>
          <p>
            Your camera records this round from the moment you press Start
            until your last answer.
          </p>
          <ol className="sk-r2-steps">
            <li>Turn on your camera and microphone with Enable camera, beside this card.</li>
            <li>
              Press <strong>Start</strong> below.
              {state?.has_warmup
                ? " A warm-up question comes first and is not scored."
                : ""}
            </li>
            <li>
              Each question rolls in here. You get thinking time, then answer
              out loud to the camera.
            </li>
            <li>
              The question stays on screen while you answer. When the time is
              up, the next question rolls in on its own. You can move on early.
            </li>
            <li>
              There {scoredTotal === 1 ? "is" : "are"} {inWords(scoredTotal)}{" "}
              {scoredTotal === 1 ? "question" : "questions"}. You cannot go back
              to an earlier one.
            </li>
          </ol>
          <p className="sk-r2-fair">
            There is no single right answer. We want to hear how you think,
            what you do first, and who you involve. You do not need to mention
            your family, relationship, health or age.
          </p>
          <button
            type="button"
            data-cy="btn-vr-begin"
            className="sk-r2-btn sk-r2-btn-primary sk-r2-btn-wide"
            disabled={!recorder.stream || busy}
            onClick={() => void beginRound()}
          >
            Start
          </button>
          {!recorder.stream && (
            <p className="sk-r2-note">Turn your camera on first.</p>
          )}
        </>
      )}
    </div>
  );

  const endCard = (
    <div className="sk-r2-pcard sk-r2-endcard" data-cy="vr-end">
      <div className="sk-r2-tick" aria-hidden>
        ✓
      </div>
      <h1>
        That&rsquo;s all {inWords(scoredTotal)}{" "}
        {scoredTotal === 1 ? "question" : "questions"}
      </h1>
      <p>
        {submitFailed
          ? "Your recording could not be submitted yet. Nothing has been lost."
          : "Thank you. Your recording is being submitted."}
      </p>
      {/* ONLY WHEN THE AUTOMATIC SUBMIT FAILED: the round submits itself
          the moment the last answer ends, and comes back to this button
          only if that could not be completed -- one attempt and no
          retake means a candidate must be able to try again. */}
      {submitFailed && (
        <button
          type="button"
          data-cy="btn-vr-submit"
          className="sk-r2-btn sk-r2-btn-primary"
          disabled={busy}
          onClick={() => void submit()}
        >
          Submit
        </button>
      )}
    </div>
  );

  const questionBlock = !!question && (
    <>
      <div className="sk-r2-dots" data-cy="vr-dots" aria-label="Progress through the questions">
        {Array.from({ length: scoredTotal }, (_, j) => (
          <i
            key={j}
            className={
              j < (state?.scored_answered ?? 0)
                ? "done"
                : state?.scored_position === j + 1
                  ? "now"
                  : ""
            }
          />
        ))}
      </div>
      {/* KEYED ON THE ITEM, so each question ROLLS IN as the design does --
          a remount replays the animation; a re-render would not. */}
      <div key={state?.index} className="sk-r2-pcard sk-r2-rollin" data-cy="vr-question">
        <div className="sk-r2-qprog" data-cy="vr-progress">
          {kicker}
        </div>
        <h2 className="sk-r2-qtitle" data-cy="vr-question-title">
          {title}
        </h2>
        <p className="sk-r2-qtext" data-cy="vr-question-text">
          {question.prompt}
          {!!question.help_text && (
            <>
              {"\n"}
              <em>{question.help_text}</em>
            </>
          )}
        </p>
      </div>
      <div className={`sk-r2-timer sk-r2-timer-${phase}`} data-cy="vr-timer" data-phase={phase}>
        <div className="sk-r2-trow">
          <span className="sk-r2-tlabel">
            <span className="sk-r2-tdot" />
            <span>
              {phase === "think" ? "Thinking time" : phase === "up" ? "Time’s up" : "Answer now"}
            </span>
          </span>
          <span
            className={`sk-r2-clock${phase === "answer" && phaseLeft < 20 ? " sk-r2-low" : ""}`}
            data-cy="vr-clock"
          >
            {remaining == null ? "--:--" : clockStr(phaseLeft)}
          </span>
        </div>
        <div className="sk-r2-ttrack">
          <div className="sk-r2-tfill" style={{ width: `${filled}%` }} />
        </div>
        {phase === "think" ? (
          <button
            type="button"
            data-cy="btn-vr-ready"
            className="sk-r2-btn sk-r2-btn-primary sk-r2-btn-wide"
            disabled={busy || !state || state.finished}
            onClick={() => void readyToAnswer()}
          >
            I&rsquo;m ready to answer
          </button>
        ) : (
          <button
            type="button"
            data-cy="btn-vr-next"
            className="sk-r2-btn sk-r2-btn-primary sk-r2-btn-wide"
            disabled={busy || !state || state.finished}
            onClick={() => void advance("next")}
          >
            {phase === "up" && !lastQuestion ? "Next question now" : finishLabel}
          </button>
        )}
      </div>
      <p className="sk-r2-note" data-cy="vr-phase-note">
        {phase === "think"
          ? `Then answer for up to ${durationWords(answerSeconds)}.`
          : phase === "up"
            ? lastQuestion
              ? "Finishing…"
              : "The next question rolls in shortly."
            : "Answer out loud to the camera. You can move on early."}
      </p>
    </>
  );

  return (
    <div className={`sk-vr sk-r2${recording ? " sk-vr-recording" : ""}`} data-cy="video-screening">
      <Modebar />
      <div className="sk-vr-page">
        {/* ONE LAYOUT FOR EVERY ITEM: the camera in a 420px column on the
            left -- the earlier screen's own card, unchanged -- and the
            round-2 questions to its right. The column folds to a slim
            panel and back, and folds by itself when the round starts. */}
        <div className="sk-vr-layout">
          <div className={`sk-vr-side${foldedHere ? " sk-vr-side-folded" : ""}`}>
          {foldedHere && (
            <div
              className={`sk-vr-rail${recording ? " sk-vr-rail-rec" : ""}`}
              data-cy="vr-camera-folded"
              data-recording={recording ? "true" : "false"}
            >
              <button
                type="button"
                data-cy="btn-vr-unfold"
                className="sk-vr-railbtn"
                onClick={() => setFolded(false)}
                title="Show the camera"
                aria-label="Show the camera"
              >
                ›
              </button>
              <span
                className={`sk-vr-dot${recording ? " sk-vr-raildot-rec" : recorder.stream ? " sk-vr-dot-on" : ""}`}
                aria-hidden
              />
              <span className="sk-vr-railtext">{recording ? "Recording" : "Standby"}</span>
              <span className="sk-vr-railclock">{mmss(recorder.elapsed)}</span>
            </div>
          )}
          {/* camera -- moved into the reading room's dock in full screen.
              HIDDEN, never unmounted, when folded: the preview keeps its
              stream and the recording never notices. */}
          <Movable to={caseId ? dock : null}>
          <div className={["sk-vr-card overflow-hidden", foldedHere ? "hidden" : ""].join(" ")}>
            <div className="sk-vr-videohead">
              <span className={`sk-vr-recbadge${recording ? " sk-vr-recbadge-on" : ""}`}>
                {recording ? "Recording" : "Standby"}
              </span>
              <span className="sk-vr-clock ml-auto text-[rgba(255,255,255,0.75)]">
                {mmss(recorder.elapsed)}
              </span>
              {running && !(caseId && dock) && (
                <button
                  type="button"
                  data-cy="btn-vr-fold"
                  className="sk-vr-foldbtn"
                  onClick={() => setFolded(true)}
                  title="Fold the camera to a side panel"
                  aria-label="Fold the camera to a side panel"
                >
                  ‹
                </button>
              )}
            </div>
            <div className="sk-vr-videobody">
              <div className="sk-vr-stage">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  data-cy="vr-preview"
                  className="block h-full w-full scale-x-[-1] object-cover"
                />
                {!recorder.stream && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3.5 bg-[#0E1613] p-6 text-center">
                    <div className="grid h-[62px] w-[62px] place-items-center rounded-full border-2 border-[rgba(255,255,255,0.22)]" />
                    <div>
                      <div className="text-[14.5px] font-semibold text-white">
                        {recorder.error ? "Camera blocked" : "Camera is off"}
                      </div>
                      <div className="mx-auto mt-1 max-w-[280px] text-[12.5px] leading-[1.5] text-[rgba(255,255,255,0.5)]">
                        {recorder.error ??
                          "We need your camera and microphone to record answers."}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-cy="btn-vr-enable"
                      className="sk-vr-btn"
                      onClick={() => void recorder.enable()}
                    >
                      Enable camera
                    </button>
                  </div>
                )}
                {recording && (
                  <div className="pointer-events-none absolute inset-0 rounded-lg border-[3px] border-[#D64545]" />
                )}
                {!!question && recorder.stream && (
                  <div className="sk-vr-caption">
                    <div className="sk-vr-kicker">{kicker}</div>
                    <div className="sk-vr-prompt" data-cy="vr-prompt">
                      {title}
                    </div>
                  </div>
                )}
              </div>

              {/* level meter, from the stream the recorder muxes */}
              <div className="mt-3.5 flex items-center gap-3">
                <span className="sk-vr-eyebrow">{recording ? "Live" : "Ready"}</span>
                <div className="sk-vr-meter" data-cy="vr-meter" aria-hidden>
                  {Array.from({ length: 44 }, (_, i) => {
                    const env = Math.sin((i / 44) * Math.PI);
                    const h = Math.max(3, recorder.level * 26 * (0.45 + env * 0.85));
                    return <span key={i} style={{ height: `${h}px` }} />;
                  })}
                </div>
              </div>

              <div className="sk-vr-note">
                <div className="sk-vr-note-h">Before you record</div>
                <div className="sk-vr-note-b">
                  Face a light source, keep your whole head in frame, and speak at
                  a normal pace. Each question is recorded once — there is no
                  retake, and you cannot return to an earlier question.
                </div>
              </div>
            </div>
          </div>
          </Movable>
          </div>

          <div className="sk-vr-main">
          {/* THE STUDY, on a case item: it takes whatever the camera does
              not. The explicit height is not decoration -- the viewer
              sizes itself to its parent. */}
          {caseId && (
            <div
              className="sk-vr-card h-[68vh] min-h-[460px] min-w-0 overflow-hidden p-3"
              data-cy="vr-case-viewer"
            >
              <RoomViewer
                source={{
                  kind: "attempt",
                  attemptId,
                  assignmentCaseId: caseId,
                }}
                testCase={{
                  title: question?.section_title || "Case",
                  modality: null,
                  clinical_history: null,
                }}
                dockSide="left"
                onDock={setDock}
                dockLabel="Camera & question"
                dockStrip={
                  <>
                    <span
                      className={`rr-strip-dot${recording ? " rr-strip-dot--rec" : ""}`}
                      data-cy="dock-strip-rec"
                      data-recording={recording ? "true" : "false"}
                      title={recording ? "Recording" : "Standby"}
                    />
                    <span className="rr-strip-clock">
                      {remaining == null ? "--:--" : clockStr(phaseLeft)}
                    </span>
                  </>
                }
                // THE CAMERA COLUMN FOLDED IN FULL SCREEN: the camera stays
                // live in it, hidden; what a candidate must still see and
                // press goes in the bar across the top.
                dockBar={
                  <div className="rr-vrbar" data-cy="vr-dockbar">
                    <span
                      className={`rr-vrbar-rec${recording ? " rr-vrbar-rec--on" : ""}`}
                    >
                      <span className={`rr-strip-dot${recording ? " rr-strip-dot--rec" : ""}`} />
                      {recording ? "Recording" : "Standby"}
                      <span className="rr-vrbar-time">{mmss(recorder.elapsed)}</span>
                    </span>
                    <span className="rr-vrbar-q">{kicker}</span>
                    <span className="rr-vrbar-prompt" data-cy="vr-dockbar-question" title={question?.prompt}>
                      {question?.prompt}
                    </span>
                    <span className={`rr-vrbar-time${recording ? " rr-vrbar-time--rec" : ""}`}>
                      {remaining == null ? "--:--" : clockStr(phaseLeft)}
                    </span>
                    <button
                      type="button"
                      data-cy="btn-vr-next-bar"
                      className="sk-vr-btn sk-vr-btn-onbar"
                      disabled={busy || !state || state.finished}
                      onClick={() => void advance("next")}
                    >
                      {finishLabel}
                    </button>
                  </div>
                }
              />
            </div>
          )}

          {/* the question -- docked under the camera in full screen */}
          <Movable to={caseId ? dock : null}>
          <div className="sk-r2-col">
            {state?.finished ? endCard : running ? questionBlock : intro}

            {!!state && state.disconnections > 0 && (
              <p className="sk-r2-note" data-cy="vr-drops">
                {state.disconnections} of {state.max_disconnections} connection
                drops used
              </p>
            )}
            {begun && (
              <p className="sk-r2-note">
                Your recording uploads as you go ·{" "}
                <span data-cy="vr-upload">
                  {recorder.pending > 0
                    ? `${recorder.pending} waiting to upload`
                    : `${recorder.uploaded} uploaded`}
                </span>
              </p>
            )}
            {running && (
              <button
                type="button"
                data-cy="btn-vr-leave"
                className="sk-r2-leave"
                onClick={() => setConfirmLeave(true)}
              >
                Leave without submitting
              </button>
            )}
          </div>
          </Movable>
          </div>
        </div>
      </div>

      <AppDialog
        open={confirmLeave}
        cy="dialog-vr-leave"
        tone="danger"
        title="Leave without submitting?"
        body="You get one attempt at this round. Everything recorded so far is kept and will be assessed, but you cannot come back and continue."
        onDismiss={() => setConfirmLeave(false)}
        actions={[
          {
            label: "Leave",
            cy: "btn-vr-confirm-leave",
            variant: "danger",
            onClick: () => {
              recorder.release();
              navigate("/tests");
            },
          },
          {
            label: "Keep answering",
            cy: "btn-vr-cancel-leave",
            variant: "ghost",
            // FOCUS LANDS HERE, not on Leave. The destructive action is
            // written first because it is what the question is about,
            // and focusing it would make Enter end a sitting somebody
            // gets one of.
            autoFocus: true,
            onClick: () => setConfirmLeave(false),
          },
        ]}
      />
    </div>
  );
}
